<div class="card rounded-lg border-0 shadow mt-2">
  <div class="card-body rounded-lg shadow">
  <div class="d-flex flex-column flex-xl-row justify-content-between align-items-xl-center gap-3 mb-4">
    <div>
      <p class="text-uppercase text-muted text-xxs mb-1">{{ display_title }}</p>
      <p class="text-x fw-bold mb-1">{{ display_subtitle }}</p>
      <p class="text-muted text-xxs mb-0">
        {{ translate_label("Review saved templates, inspect the setup, or jump straight into quick generation. PDF templates always generate as PDF.") }}
      </p>
    </div>
    <div class="d-flex flex-column flex-sm-row gap-2 align-items-sm-center">
      <button
        class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
        :disabled="loading_templates"
        type="button"
        @click="load_templates(page_number)"
      >
        Refresh
      </button>
    </div>
  </div>

  <div class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-2 mb-3">
    <div class="d-flex align-items-center gap-2">
      <div v-if="searching_templates" class="d-flex align-items-center gap-1 text-muted text-xxs">
        <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
        <span>Searching...</span>
      </div>
    </div>
    <div class="d-flex flex-column flex-sm-row gap-2 align-items-sm-center">
      <input
        v-model.trim="search_query"
        class="modal-form-control form-control text-xxs rounded-small p-2 shadow-sm"
        :placeholder="translate_label('Search templates...')"
        type="text"
        @input="schedule_search"
      />
    </div>
  </div>

  <div class="row g-3 mb-4">
    <div v-for="card in summary_cards" :key="card.label" class="col-12 col-md-4">
      <div class="card shadow-sm border rounded-lg p-3 h-100">
        <p class="text-uppercase text-muted text-xxs mb-1">{{ card.label }}</p>
        <p class="fw-bold text-dark text-xxs mb-1">{{ card.value }}</p>
        <p class="text-muted text-xxs mb-0">{{ card.description }}</p>
      </div>
    </div>
  </div>

  <div v-if="last_generated_report" class="card shadow-sm border rounded-lg p-3 mb-4">
    <p class="fw-bold text-dark text-xxs mb-1">{{ last_generated_report.name }}</p>
    <p class="text-muted text-xxs mb-2">
      Status: {{ get_last_generated_status_label() }}
    </p>
    <div class="d-flex gap-2">
      <a
        v-if="last_generated_report.presigned_url"
        :href="last_generated_report.presigned_url"
        class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
        rel="noopener noreferrer"
        target="_blank"
      >
        Download report
      </a>
      <span v-else-if="last_generated_report.error_message" class="text-danger text-xxs">
        {{ last_generated_report.error_message }}
      </span>
    </div>
  </div>

  <div class="rounded-lg p-2">
    <ServerPaginatedTable
        :headers="template_table_headers"
        :data="table_safe_templates"
        :loading="loading_templates"
        :next_page="next_page"
        :page_number="page_number"
        :page_size="page_size"
        :prev_page="prev_page"
        :total_count="total_count"
        :total_pages="total_pages"
        :empty_message="translate_label('No saved templates found.')"
        :loading_text="translate_label('Loading templates...')"
        table_id="report_template_library_table"
        @page-change="go_to_page"
    >
      <template #actions="{ row }">
        <button
          class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
          data-bs-toggle="modal"
          data-bs-target="#report_template_details"
          type="button"
          @click="open_template_details(row)"
        >
          View
        </button>
        <button
          class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
          type="button"
          @click="edit_template(row)"
        >
          Edit
        </button>
        <button
          class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
          :disabled="is_quick_generate_disabled(row)"
          type="button"
          @click="quick_generate(row)"
        >
          <span
            v-if="generating_template_id === build_template_row_key(row)"
            class="spinner-border spinner-border-sm me-2"
          ></span>
          {{
            generating_template_id === build_template_row_key(row)
              ? "Generating..."
              : build_quick_generate_label(row)
          }}
        </button>
      </template>
    </ServerPaginatedTable>
  </div>

  <teleport to="body">
    <div class="modal fade" id="report_template_details" tabindex="-1" aria-labelledby="report_template_details_label" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-xl modal-dialog-scrollable">
        <div class="modal-content bg-white rounded-3 p-0 shadow-lg">
          <div class="modal-header border-bottom-0 cute-header shadow-lg">
            <div>
              <p class="modal-title text-white text-xxs mb-1" id="report_template_details_label">
                {{ selected_template_record?.name || translate_label("Template Details") }}
              </p>
              <p class="text-white text-xxs mb-0">
                <template v-if="is_selected_pdf_template">
                  {{ translate_label("PDF Report") }} | {{ format_pdf_generation_mode(selected_template_record?.generation_mode) }}
                </template>
                <template v-else>
                  {{ selected_template_record?.report_type?.display_name || "-" }} | {{ selected_template_record?.scope_type?.display_name || "-" }}
                </template>
              </p>
            </div>
            <button type="button" class="modal-btn-close btn-close" data-bs-dismiss="modal" aria-label="Close" @click="close_template_details"></button>
          </div>
          <div class="modal-body">
            <div class="row g-3 mb-4">
              <div class="col-12 col-md-6">
                <div class="card shadow-sm border rounded-lg p-3 h-100">
                  <p class="fw-bold text-xxs mb-1">{{ translate_label("Description") }}</p>
                  <p class="text-muted text-xxs mb-0">{{ selected_template_record?.description || translate_label("No description added.") }}</p>
                </div>
              </div>
              <div class="col-12 col-md-6">
                <div class="card shadow-sm border rounded-lg p-3 h-100">
                  <p class="fw-bold text-xxs mb-1">{{ translate_label(is_selected_pdf_template ? "Generation" : "Entity Types") }}</p>
                  <p class="text-muted text-xxs mb-0">
                    {{
                      is_selected_pdf_template
                        ? format_pdf_generation_mode(selected_template_record?.generation_mode)
                        : selected_template_record
                          ? format_entity_types(selected_template_record)
                          : "-"
                    }}
                  </p>
                </div>
              </div>
            </div>

            <template v-if="is_selected_pdf_template">
              <div class="card shadow-sm border rounded-lg p-3 mb-4">
                <p class="fw-bold text-xxs mb-3">{{ translate_label("Branding") }}</p>
                <div class="row g-3">
                  <div class="col-12 col-md-6">
                    <p class="text-muted text-xxs mb-1">{{ translate_label("Heading") }}</p>
                    <p class="text-dark text-xxs mb-0">{{ selected_template_record?.header_title || "-" }}</p>
                  </div>
                  <div class="col-12 col-md-6">
                    <p class="text-muted text-xxs mb-1">{{ translate_label("Subtitle") }}</p>
                    <p class="text-dark text-xxs mb-0">{{ selected_template_record?.header_subtitle || "-" }}</p>
                  </div>
                  <div class="col-12 col-md-6">
                    <p class="text-muted text-xxs mb-1">{{ translate_label("Page Header") }}</p>
                    <p class="text-dark text-xxs mb-0">{{ selected_template_record?.header_text || "-" }}</p>
                  </div>
                  <div class="col-12 col-md-6">
                    <p class="text-muted text-xxs mb-1">{{ translate_label("Footer") }}</p>
                    <p class="text-dark text-xxs mb-0">{{ selected_template_record?.footer_text || "-" }}</p>
                  </div>
                </div>
              </div>

              <div class="rounded-lg p-2 mb-4">
                <p class="fw-bold text-dark text-xxs px-2 pt-2 mb-2">{{ translate_label("Sections") }}</p>
                <ServerPaginatedTable
                  :headers="detail_pdf_section_headers"
                  :data="detail_pdf_sections"
                  :show_pagination="false"
                  table_id="report_template_pdf_sections_table"
                />
              </div>

              <div v-if="detail_entity_types.length" class="rounded-lg p-2">
                <p class="fw-bold text-dark text-xxs px-2 pt-2 mb-2">{{ translate_label("Assigned Entity Types") }}</p>
                <ServerPaginatedTable
                  :headers="detail_entity_type_headers"
                  :data="detail_entity_types"
                  :show_pagination="false"
                  table_id="report_template_entity_types_table"
                />
              </div>
            </template>

            <template v-else>
              <div class="rounded-lg p-2 mb-4">
                <p class="fw-bold text-dark text-xxs px-2 pt-2 mb-2">{{ translate_label("Columns") }}</p>
                <ServerPaginatedTable
                  :headers="detail_column_headers"
                  :data="detail_columns"
                  :show_pagination="false"
                  table_id="report_template_columns_table"
                />
              </div>

              <div class="rounded-lg p-2 mb-4">
                <p class="fw-bold text-dark text-xxs px-2 pt-2 mb-2">{{ translate_label("Question Scope And Answer Filters") }}</p>
                <ServerPaginatedTable
                  :headers="detail_question_filter_headers"
                  :data="detail_question_filters"
                  :show_pagination="false"
                  table_id="report_template_questions_table"
                />
              </div>

              <div v-if="detail_question_metrics.length" class="rounded-lg p-2 mb-4">
                <p class="fw-bold text-dark text-xxs px-2 pt-2 mb-2">{{ translate_label("Question Statistics") }}</p>
                <ServerPaginatedTable
                  :headers="detail_question_metric_headers"
                  :data="detail_question_metrics"
                  :show_pagination="false"
                  table_id="report_template_question_metrics_table"
                />
              </div>

              <div v-if="detail_other_filters.length" class="rounded-lg p-2 mb-4">
                <p class="fw-bold text-dark text-xxs px-2 pt-2 mb-2">{{ translate_label("Other Filters") }}</p>
                <ServerPaginatedTable
                  :headers="detail_other_filter_headers"
                  :data="detail_other_filters"
                  :show_pagination="false"
                  table_id="report_template_other_filters_table"
                />
              </div>

              <div v-if="detail_entity_types.length" class="rounded-lg p-2">
                <p class="fw-bold text-dark text-xxs px-2 pt-2 mb-2">{{ translate_label("Assigned Entity Types") }}</p>
                <ServerPaginatedTable
                  :headers="detail_entity_type_headers"
                  :data="detail_entity_types"
                  :show_pagination="false"
                  table_id="report_template_entity_types_table"
                />
              </div>
            </template>
          </div>
          <div class="modal-footer border-0 p-2 d-flex justify-content-end">
            <button
              type="button"
              class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
              data-bs-dismiss="modal"
              @click="close_template_details"
            >
              {{ translate_label("Close") }}
            </button>
            <button
              type="button"
              class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
              data-bs-dismiss="modal"
              @click="selected_template && edit_template(selected_template)"
            >
              {{ translate_label("Edit In Builder") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </teleport>

  <PDFReportSignatureModal
    v-if="signature_modal_visible"
    @close="close_signature_modal"
    @confirm="confirm_signature"
  />
  </div>
</div>
